import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { FormattedMessage } from "react-intl";

import { Link } from "react-router-dom";

import ImmutablePropTypes from "react-immutable-proptypes";

import { openModal } from "flavours/glitch/actions/modal";
import AttachmentList from "flavours/glitch/components/attachment_list";
import { Avatar } from "flavours/glitch/components/avatar";
import { DisplayName } from "flavours/glitch/components/display_name";
import MediaGallery from "flavours/glitch/components/media_gallery";
import { resolveQuoteUrl } from "flavours/glitch/actions/quotes";
import Video from "flavours/glitch/features/video";
import { useAppDispatch, useAppSelector } from "flavours/glitch/store";

const PLACEHOLDERS = {
  pending: <FormattedMessage id='status.quote.pending' defaultMessage='Waiting for the author to approve this quote' />,
  deleted: <FormattedMessage id='status.quote.deleted' defaultMessage='The quoted post has been deleted' />,
  blocked_account: <FormattedMessage id='status.quote.blocked' defaultMessage='Hidden because you blocked or muted this account' />,
  blocked_domain: <FormattedMessage id='status.quote.blocked' defaultMessage='Hidden because you blocked or muted this account' />,
  muted_account: <FormattedMessage id='status.quote.blocked' defaultMessage='Hidden because you blocked or muted this account' />,
};

const UNAVAILABLE = <FormattedMessage id='status.quote.unavailable' defaultMessage='This quoted post is not available' />;

const QuoteBox = ({ children }) => (
  <div className='status-quote'>{children}</div>
);

QuoteBox.propTypes = {
  children: PropTypes.node,
};

const QuoteMedia = ({ status }) => {
  const dispatch = useAppDispatch();
  const letterbox = useAppSelector(store => store.getIn(["local_settings", "media", "letterbox"]));
  const useBlurhash = useAppSelector(store => store.getIn(["local_settings", "media", "use_blurhash"]));
  const previewOnly = useAppSelector(store => store.getIn(["local_settings", "media", "preview_only"]));
  const showAltBadge = useAppSelector(store => store.getIn(["local_settings", "media", "show_alt_badge"]));
  const autoplayMuted = useAppSelector(store => store.getIn(["local_settings", "media", "autoplay_muted"]));

  const attachments = status.get("media_attachments");
  const language = status.getIn(["translation", "language"]) || status.get("language");
  const statusId = status.get("id");
  const type = attachments.getIn([0, "type"]);

  if (type === "image" || type === "gifv") {
    return (
      <MediaGallery
        media={attachments}
        lang={language}
        sensitive={status.get("sensitive")}
        letterbox={letterbox}
        useBlurhash={useBlurhash}
        showAltBadge={showAltBadge}
        previewOnly={previewOnly}
        onOpenMedia={(media, index) => dispatch(openModal({ modalType: "MEDIA", modalProps: { statusId, media, index, lang: language } }))}
      />
    );
  }

  if (type === "video") {
    const attachment = attachments.get(0);

    return (
      <Video
        preview={attachment.get("preview_url")}
        frameRate={attachment.getIn(["meta", "original", "frame_rate"])}
        blurhash={attachment.get("blurhash")}
        src={attachment.get("url")}
        alt={attachment.getIn(["translation", "description"]) || attachment.get("description")}
        lang={language}
        inline
        sensitive={status.get("sensitive")}
        letterbox={letterbox}
        useBlurhash={useBlurhash}
        autoplayMuted={autoplayMuted}
        onOpenVideo={(options) => dispatch(openModal({ modalType: "VIDEO", modalProps: { statusId, media: attachment, lang: language, options } }))}
      />
    );
  }

  return <AttachmentList compact media={attachments} />;
};

QuoteMedia.propTypes = {
  status: ImmutablePropTypes.map.isRequired,
};

export const StatusQuote = ({ quote }) => {
  const state = quote.get("state");
  const url = quote.get("url");
  const dispatch = useAppDispatch();
  const [resolved, setResolved] = useState(undefined);
  const quotedId = quote.get("quoted_status") || resolved;
  const status = useAppSelector(store => quotedId ? store.getIn(["statuses", quotedId]) : null);
  const account = useAppSelector(store => status ? store.getIn(["accounts", status.get("account")]) : null);

  useEffect(() => {
    if (quote.get("quoted_status") || !url) {
      return undefined;
    }

    let cancelled = false;

    dispatch(resolveQuoteUrl(url)).then(id => {
      if (!cancelled) {
        setResolved(id);
      }

      return id;
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [dispatch, quote, url]);

  if (state !== "accepted") {
    return <QuoteBox><span className='status-quote__placeholder'>{PLACEHOLDERS[state] || UNAVAILABLE}</span></QuoteBox>;
  }

  if (!status || !account) {
    if (url) {
      return (
        <QuoteBox>
          <a className='status-quote__placeholder' href={url} target='_blank' rel='noopener noreferrer'>{resolved === undefined ? <FormattedMessage id='status.quote.loading' defaultMessage='Loading quoted post…' /> : url}</a>
        </QuoteBox>
      );
    }

    return <QuoteBox><span className='status-quote__placeholder'>{UNAVAILABLE}</span></QuoteBox>;
  }

  const spoiler = status.get("spoiler_text");
  const attachments = status.get("media_attachments");

  return (
    <QuoteBox>
      <Link className='status-quote__header' to={`/@${account.get("acct")}/${status.get("id")}`}>
        <Avatar account={account} size={24} />
        <DisplayName account={account} />
      </Link>

      {spoiler ? (
        <div className='status-quote__content status__content translate'>{spoiler}</div>
      ) : (
        <div className='status-quote__content status__content translate' dangerouslySetInnerHTML={{ __html: status.get("contentHtml") || status.get("content") || "" }} />
      )}

      {!spoiler && attachments && attachments.size > 0 && (
        <QuoteMedia status={status} />
      )}
    </QuoteBox>
  );
};

StatusQuote.propTypes = {
  quote: ImmutablePropTypes.map.isRequired,
};

export default StatusQuote;
